import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    Grid,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Box,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { HighlightOff } from '@mui/icons-material';
import axios from 'axios';
import { aiProductScore } from '../../services/ai_service';
import { ScoreRing } from './ProductScoreCard';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    // Use a z-index below MUI's default (1300) so enrichment dialogs layer on top
    zIndex: 1200,
    "& .MuiPaper-rounded": {
        borderRadius: "12px",
        padding: "16px"
    },
    "& .MuiDialogTitle-root": {
        padding: 0
    },
    "& .MuiDialogContent-root": {
        padding: 0
    }
}));

const NON_ENRICHABLE_ATTRIBUTES = ['category'];

/**
 * Parse a suggestion object from the API into { attribute, issue, recommendation }.
 * API returns: { Field: string, Issue: string, Recommendation: string }
 */
type ParsedSuggestion = { attribute: string | null; issue: string; recommendation: string };

const parseSuggestion = (item: unknown): ParsedSuggestion => {
    if (typeof item === 'object' && item !== null) {
        const suggestion = item as { Field?: string; Issue?: string; Recommendation?: string };
        return {
            attribute: suggestion.Field || null,
            issue: suggestion.Issue || '',
            recommendation: suggestion.Recommendation || ''
        };
    }
    // Fallback for plain strings
    return { attribute: null, issue: String(item), recommendation: '' };
};

/**
 * Product Score Dialog.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - attributes: object — product.Attributes
 * - onEnrichAttribute: (attributeName: string) => void — opens enrichment dialog
 * - onScoreReceived: (score: number, suggestions: string[]) => void — callback when score returns
 * - initialScore: number|null — cached score to display without re-fetching
 * - initialSuggestions: array — cached suggestions to display without re-fetching
 * - rescore: boolean — if true, fetch fresh score from API; if false, display cached data
 */
interface ProductScoreDialogProps {
    open: boolean;
    onClose: () => void;
    attributes: Record<string, unknown>;
    availableAttributes?: Array<{ Name: string; Type?: string }>;
    onEnrichAttribute: (attributeName: string) => void;
    onScoreReceived?: (score: number, suggestions: unknown[]) => void;
    initialScore?: number | null;
    initialSuggestions?: unknown[];
    rescore?: boolean;
    acceptedAttributes?: Set<string>;
    retailerCoid?: number;
}

function ProductScoreDialog({ open, onClose, attributes, availableAttributes, onEnrichAttribute, onScoreReceived, initialScore, initialSuggestions, rescore, acceptedAttributes, retailerCoid }: ProductScoreDialogProps) {
    const theme = useTheme();
    const [score, setScore] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const cancelSourceRef = useRef(null);

    useEffect(() => {
        if (open) {
            if (rescore) {
                fetchScore();
            } else {
                // Display cached data
                setScore(initialScore);
                setSuggestions(initialSuggestions || []);
            }
        }
        return () => {
            if (cancelSourceRef.current) {
                cancelSourceRef.current.cancel('Dialog closed');
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const fetchScore = async () => {
        if (cancelSourceRef.current) {
            cancelSourceRef.current.cancel('New request');
        }
        const source = axios.CancelToken.source();
        cancelSourceRef.current = source;

        setIsLoading(true);
        setError(null);
        try {
            const result = await aiProductScore(attributes, availableAttributes, source.token, retailerCoid);
            if (result.success) {
                setScore(result.score);
                setSuggestions(result.suggestions);
                if (onScoreReceived) {
                    onScoreReceived(result.score, result.suggestions);
                }
            } else {
                setError(result.error || 'Failed to score product');
            }
        } catch (err) {
            if (axios.isCancel(err)) return;
            console.error('Product score error:', err);
            setError('An error occurred while scoring the product');
        } finally {
            if (cancelSourceRef.current === source) {
                setIsLoading(false);
            }
        }
    };

    // Build a map of lowercase attribute name -> Type for cross-checking AI suggestions
    const attributeTypeMap = availableAttributes
        ? new Map(availableAttributes.map(a => [a.Name.toLowerCase(), a.Type]))
        : null;
    const isEnrichable = (fieldName) => {
        if (!fieldName) return false;
        if (NON_ENRICHABLE_ATTRIBUTES.includes(fieldName.toLowerCase())) return false;
        if (!attributeTypeMap) return true;
        const type = attributeTypeMap.get(fieldName.toLowerCase());
        return type === 'text';
    };
    const parsed = suggestions.map(parseSuggestion);
    const percentage = score !== null ? Math.round(score * 10) : 0;

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Typography style={{ fontSize: "20px", fontWeight: 600 }}>
                        Product Readiness Score
                    </Typography>
                    <IconButton
                        style={{ height: "20px", width: "20px" }}
                        onClick={onClose}
                        size="large"
                    >
                        <HighlightOff style={{ color: "red" }} />
                    </IconButton>
                </Grid>
            </DialogTitle>
            <DialogContent>
                <Grid container style={{ marginTop: "24px" }}>
                    {isLoading ? (
                        <Grid item container style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "16px",
                            margin: "10px 0",
                            width: "100%"
                        }}>
                            <LinearProgress style={{ width: '100%' }} />
                            <Typography variant="body2" color="textSecondary">
                                Scoring your product...
                            </Typography>
                        </Grid>
                    ) : error ? (
                        <Grid item container style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "10px 0",
                            width: "100%"
                        }}>
                            <Typography variant="body1" color="error">
                                {error}
                            </Typography>
                            <Button
                                variant="text"
                                onClick={fetchScore}
                                style={{ marginTop: '16px' }}
                            >
                                Try Again
                            </Button>
                        </Grid>
                    ) : (
                        <>
                            {/* Score Display */}
                            <Grid item container style={{
                                marginBottom: "24px",
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                width: "100%"
                            }}>
                                <Box style={{ flex: 1 }}>
                                    <Typography variant="h6">
                                        Your product scored {percentage}% out of 100%.
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>
                                        This score is based on the consistency, completeness, and quality of your product data.
                                        Improvements can help your products perform better across sales channels and AI agents.
                                    </Typography>
                                </Box>
                                <ScoreRing percentage={percentage} size={70} />
                            </Grid>

                            {/* Recommendations Table */}
                            {parsed.length > 0 && (
                                <Grid item container style={{ width: "100%", marginBottom: "16px" }}>
                                    <Typography style={{
                                        fontSize: "16px",
                                        fontWeight: 600,
                                        marginBottom: "8px",
                                        textAlign: "left",
                                        width: "100%"
                                    }}>
                                        Recommendations
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Attribute</TableCell>
                                                <TableCell>Issue</TableCell>
                                                <TableCell>Recommendation</TableCell>
                                                <TableCell align="center">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {parsed.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                                                            {item.attribute || '(General)'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {item.issue}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {item.recommendation}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {item.attribute && isEnrichable(item.attribute) ? (
                                                            acceptedAttributes && acceptedAttributes.has(item.attribute) ? (
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    disabled
                                                                    sx={{
                                                                        backgroundColor: 'success.main',
                                                                        color: 'white',
                                                                        '&.Mui-disabled': {
                                                                            backgroundColor: 'success.main',
                                                                            color: 'white',
                                                                        }
                                                                    }}
                                                                >
                                                                    Accepted
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    size="small"
                                                                    onClick={() => onEnrichAttribute(item.attribute)}
                                                                >
                                                                    Enrich
                                                                </Button>
                                                            )
                                                        ) : (
                                                            <Typography variant="caption" color="textSecondary">
                                                                Not enrichable, please manually address
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>
                            )}

                            {/* Advisory Disclaimer */}
                            <Box style={{
                                width: "100%",
                                padding: "8px 0",
                                borderTop: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="caption" color="textSecondary">
                                    Scores are advisory feedback only and are not automatically stored.
                                </Typography>
                            </Box>
                        </>
                    )}

                </Grid>
            </DialogContent>
        </StyledDialog>
    );
}

export default ProductScoreDialog;
